import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  RefreshCw,
  Users,
  Trash2,
  Eye,
  X,
  Briefcase,
  FileText,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";

import {
  useCreateCommitteeMutation,
  useDeleteCommitteeMutation,
  useGetCommitteesQuery,
} from "../api/committeesApi";

import { PERMS } from "../../../utils/perms";
import AccessGate from "../../../components/AccessGate";
import { useAccess } from "../../../utils/useAccess";
import PageGate from "../../../components/PageGate";

function StatCard({
  title,
  value,
  icon: Icon,
  color,
}: {
  title: string;
  value: string | number;
  icon: any;
  color: string;
}) {
  return (
    <div className="bg-white border rounded-xl p-5 shadow-sm">
      <div className="flex justify-between items-start">
        <div>
          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
            {title}
          </div>
          <div className="text-2xl font-black text-gray-900 mt-1">{value}</div>
        </div>
        <div className={`p-2 rounded-lg ${color}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
    </div>
  );
}

function msgFromErr(err: any) {
  return err?.data?.message || err?.error || "Request failed";
}

export default function CommitteesPage() {
  const { user } = useAccess();
  const { data: res, isLoading, error, refetch } = useGetCommitteesQuery();
  const [createCommittee, { isLoading: creating }] =
    useCreateCommitteeMutation();
  const [deleteCommittee, { isLoading: deleting }] =
    useDeleteCommitteeMutation();

  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const [form, setForm] = useState({
    name: "",
    description: "",
    is_active: true,
  });
  const [formErr, setFormErr] = useState<{ name?: string }>({});
  const nameRef = useRef<HTMLInputElement | null>(null);

  const list = useMemo(() => res || [], [res]);

  useEffect(() => {
    if (open) setTimeout(() => nameRef.current?.focus(), 0);
  }, [open]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setMsg(null);

    if (!form.name.trim()) {
      setFormErr({ name: "Committee name is required" });
      nameRef.current?.focus();
      return;
    }

    try {
      const result: any = await createCommittee({
        name: form.name.trim(),
        description: form.description?.trim() || undefined,
        is_active: form.is_active,
      }).unwrap();

      setMsg({ type: "success", text: result?.message || "Committee created" });
      setOpen(false);
      setForm({ name: "", description: "", is_active: true });
    } catch (err: any) {
      setMsg({ type: "error", text: msgFromErr(err) });
    }
  };

  const onDelete = async (id: number) => {
    if (
      !window.confirm(
        "Delete this committee? This will also delete its unique system role."
      )
    )
      return;
    try {
      await deleteCommittee(id).unwrap();
      setMsg({ type: "success", text: "Committee removed" });
    } catch (err: any) {
      setMsg({ type: "error", text: msgFromErr(err) });
    }
  };

  return (
    <PageGate perm={PERMS.committee.access}>
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white border rounded-xl p-5 shadow-sm">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-indigo-600">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-black text-gray-900 tracking-tight text-xl">
                  Committees
                </h1>
                <p className="text-sm text-gray-500">
                  Manage organizational working groups
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => refetch()}
                className="btn-secondary"
                disabled={isLoading}
              >
                <RefreshCw
                  className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`}
                />
              </button>
              <AccessGate perm={PERMS.committee.create}>
                <button
                  onClick={() => setOpen(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white font-bold text-sm shadow-lg shadow-gray-900/20 active:scale-95 transition-all"
                >
                  <Plus className="w-4 h-4" /> New Committee
                </button>
              </AccessGate>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total"
            value={list.length}
            icon={Users}
            color="bg-blue-50 text-blue-600"
          />
          <StatCard
            title="Active"
            value={list.filter((c: any) => c.is_active).length}
            icon={CheckCircle2}
            color="bg-green-50 text-green-600"
          />
          <StatCard
            title="Inactive"
            value={list.filter((c: any) => !c.is_active).length}
            icon={AlertCircle}
            color="bg-red-50 text-red-600"
          />
        </div>

        {msg && (
          <div
            className={`p-4 rounded-xl border flex justify-between items-center ${
              msg.type === "success"
                ? "bg-green-50 border-green-100 text-green-800"
                : "bg-red-50 border-red-100 text-red-800"
            }`}
          >
            <span className="text-sm font-bold">{msg.text}</span>
            <button onClick={() => setMsg(null)}>
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Table */}
        <div className="bg-white border rounded-xl overflow-hidden shadow-sm">
          <table className="w-full text-sm text-left">
            <thead>
              <tr className="border-b bg-gray-50/50 text-[10px] font-black uppercase tracking-widest text-gray-400">
                <th className="py-4 px-6">Committee Name</th>
                <th className="py-4 px-6">System Role</th>
                <th className="py-4 px-6">Executive Set</th>
                <th className="py-4 px-6">Status</th>
                <th className="py-4 px-6 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {list.map((c: any) => (
                <tr
                  key={c.id}
                  className="hover:bg-gray-50/50 transition-colors"
                >
                  <td className="py-4 px-6">
                    <div className="font-bold text-gray-900">{c.name}</div>
                    <div className="text-[11px] text-gray-400">{c.slug}</div>
                  </td>
                  <td className="py-4 px-6">
                    <span className="px-2 py-1 rounded-md bg-indigo-50 text-indigo-600 font-mono text-[10px] font-bold">
                      {c.role?.name || "no_role_sync"}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-gray-500 font-medium">
                    {c.executive_set?.name || "N/A"}
                  </td>
                  <td className="py-4 px-6">
                    <span
                      className={`px-2 py-1 rounded-full text-[10px] font-black uppercase ${
                        c.is_active
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {c.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-right">
                    <div className="flex justify-end gap-2">
                      <Link
                        to={`/committees/${c.id}`}
                        className="p-2 border rounded-lg hover:bg-gray-50 text-gray-600"
                      >
                        <Eye className="w-4 h-4" />
                      </Link>
                      <AccessGate perm={PERMS.committee.delete}>
                        <button
                          onClick={() => onDelete(c.id)}
                          className="p-2 border rounded-lg hover:bg-red-50 text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </AccessGate>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Create Modal */}
        {open && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 backdrop-blur-sm p-4">
            <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl p-6 animate-in zoom-in duration-200">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-black text-gray-900">
                  New Committee
                </h2>
                <button onClick={() => setOpen(false)}>
                  <X className="text-gray-400" />
                </button>
              </div>

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 block">
                    Committee Name
                  </label>
                  <input
                    ref={nameRef}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full border rounded-xl px-4 py-3 focus:border-gray-900 outline-none transition-all"
                    placeholder="e.g. Audit & Risk Committee"
                  />
                  {formErr.name && (
                    <p className="text-xs text-red-600 mt-1">{formErr.name}</p>
                  )}
                </div>

                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 block">
                    Description
                  </label>
                  <textarea
                    value={form.description}
                    onChange={(e) =>
                      setForm({ ...form, description: e.target.value })
                    }
                    className="w-full border rounded-xl px-4 py-3 focus:border-gray-900 outline-none transition-all resize-none"
                    rows={3}
                  />
                </div>

                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) =>
                      setForm({ ...form, is_active: e.target.checked })
                    }
                    className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm font-bold text-gray-700">
                    Set as Active on creation
                  </span>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="flex-1 px-4 py-3 rounded-xl border font-bold text-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={creating}
                    className="flex-1 px-4 py-3 rounded-xl bg-gray-900 text-white font-bold hover:bg-gray-800 disabled:opacity-50"
                  >
                    {creating ? "Creating..." : "Create Committee"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </PageGate>
  );
}
